import { Component, Input, OnInit } from '@angular/core';
import { UntypedFormGroup, UntypedFormControl, Validators } from '@angular/forms';
import { KeyValuePair } from '../../../shared/key-value-pair';
import { StateConstants } from '../../../shared/constants/state-constants';
import { State } from '../../../shared/state';
import { FormValidation } from '../../../shared/forms/form-validation';
import { UserCache } from "../../claims-app/services/user/user.cache";
import { takeUntil } from "rxjs/operators";
import { Subject } from "rxjs";
import { FormUtility } from "../../../shared/forms/form-utility";

@Component({
  selector: 'claim-repair-facility',
  templateUrl: './claim-repair-facility.component.html',
})
export class ClaimRepairFacilityComponent implements OnInit {
  
  private readonly _destroying$ = new Subject<void>();
  
  public claimRepairFacilityForm: UntypedFormGroup;
  public formCompleted = false;
  
  public manualKeyValuePairs: KeyValuePair[] = [{ key: "Mitchell", value: "Mitchell" }, { key: "AllData", value: "AllData" }, { key: "Motors", value: "Motors" }, { key: "Other", value: "Other" }];
  public payMethods: KeyValuePair[] = [{ key: "CC By Email", value: "Credit Card by Email" }, { key: "Check", value: "Check" }];
  public states: State[] = [];
  
  constructor(private userCache: UserCache) { }

  ngOnInit(): void {
    this.getStates();
    this.populateData();

    this.userCache.updated
        .pipe(takeUntil(this._destroying$))
        .subscribe(() => {
          this.updateFormData();
        });
    this.updateFormData();
  }

  public getStates(): void {
    this.states = StateConstants.GetAllUsStates();
  }

  public populateData() {
    this.claimRepairFacilityForm = new UntypedFormGroup({
      'repairFacilityName': new UntypedFormControl("", [Validators.required]),
      'businessEmailAddress': new UntypedFormControl("", [Validators.required, Validators.email]),
      'contactEmailAddress': new UntypedFormControl("", [Validators.required, Validators.email]),
      'streetAddress1': new UntypedFormControl("", [Validators.required]),
      'streetAddress2': new UntypedFormControl(),
      'city': new UntypedFormControl("", [Validators.required]),
      'state': new UntypedFormControl("", [Validators.required]),
      'zip': new UntypedFormControl("", [Validators.required]),
      'laborRatePerHour': new UntypedFormControl("", [Validators.required]),
      'laborManualUsed': new UntypedFormControl(),
      'phoneNumber': new UntypedFormControl("", [Validators.required]),
      'preferredPaymentMethod': new UntypedFormControl(),
      'repairFacilityContactName': new UntypedFormControl(),
    });
  }

  /**
   * Updates form data from the cache.
   * @see UserCache
    */
  public updateFormData(): void
  {
    if (this.userCache.selectedRepairFacility != undefined) {
      // Set repair facility information.
      this.claimRepairFacilityForm.get('repairFacilityName').setValue(this.userCache.selectedRepairFacility?.facilityName);
      this.claimRepairFacilityForm.get('businessEmailAddress').setValue(this.userCache.selectedRepairFacility?.emailAddress);
      this.claimRepairFacilityForm.get('streetAddress1').setValue(this.userCache.selectedRepairFacility?.address?.addressLine1);
      this.claimRepairFacilityForm.get('streetAddress2').setValue(this.userCache.selectedRepairFacility?.address?.addressLine2);
      this.claimRepairFacilityForm.get('city').setValue(this.userCache.selectedRepairFacility?.address?.city);
      this.claimRepairFacilityForm.get('zip').setValue(this.userCache.selectedRepairFacility?.address?.zip);
      this.claimRepairFacilityForm.get('laborRatePerHour').setValue(this.userCache.selectedRepairFacility?.laborRate);
      this.claimRepairFacilityForm.get('laborManualUsed').setValue(this.userCache.selectedRepairFacility?.laborManual);

      // Look up state.
      const stateValue = this.userCache.selectedRepairFacility?.address?.state;
      const state = this.states.find(x => x.name == stateValue || x.abbreviation == stateValue)?.name;
      this.claimRepairFacilityForm.get('state').setValue(state);

      // Look up payment method.
      //TODO: Put payment methods in a type. (Currently hard-coded in UI.)
      const userPayMethod = this.userCache.selectedRepairFacility?.preferredPaymentMethod;
      let payMethod = this.payMethods.find(x => x.key == userPayMethod || x.value == userPayMethod)?.key;
      this.claimRepairFacilityForm.get('preferredPaymentMethod').setValue(payMethod);

      this.claimRepairFacilityForm.markAsDirty();
    }
    
    if (this.userCache.selectedContact != undefined) {
      // Set contact information.
      this.claimRepairFacilityForm.get('repairFacilityContactName').setValue(this.userCache.selectedContact?.name);
      this.claimRepairFacilityForm.get('contactEmailAddress').setValue(this.userCache.selectedContact?.emailAddress);
      this.claimRepairFacilityForm.get('phoneNumber').setValue(this.userCache.selectedContact?.phone);

      this.claimRepairFacilityForm.markAsDirty();
    }
  }

  public validateForm(): void {
    this.formCompleted = FormValidation.isValid(this.claimRepairFacilityForm);
  }

  /**
   * Resets the form.
   * @see {@link FormUtility.reset}
   */
  public reset(): void {
    FormUtility.reset(this.claimRepairFacilityForm);
    this.formCompleted = false;
    this.updateFormData();
  }

  /**
   * Clears the value in the specified form field.
   * @param field The form field to clear.
   */
  public clear(field: string): void {
    FormUtility.clear(this.claimRepairFacilityForm, field);
  }

  /**
   * Gets a value indicating if the specified field has a value.
   * @param field The form field to check.
   * @returns If the field has a value, `true`; otherwise, `false`.
   */
  public hasValue(field: string): boolean {
    return FormUtility.hasValue(this.claimRepairFacilityForm, field)
  }
}